export enum PeblSendType {
  NONE = 'NONE',
  EMAIL = 'email',
  LINK = 'manual',
  SMS = 'sms',
}

export class PEBLPaymentResponse {
  public message: string = '';
  public token: string = '';

  public Build(_token: string): PEBLPaymentResponse {
    this.token = _token;
    return this;
  }
}

export class PEBLFormData {
  public name: string = '';
  public monekId: string = '';
  public buid: string = '';
  public amount: number = 0; // major
  public paymentDetail: string = '';
  public currencyCode: string = '826';
  public countryCode: string = '826';
  public settle: string = '';
  public cof: boolean = false;

  public method: PeblSendType = PeblSendType.LINK;
  public preparedPaymentMinutes: string = '';
  public phoneNumber: string = '';
  public emailAddress: string = '';

  public OperatorId: string;
  public OperatorName: string;
  public OperatorEmail: string;
  public merchantName: string;

  public scheduleEnabled: boolean = false;
  public scheduleMode: 'once' | 'recurring' = 'once';
  public sendAt?: Date;
  public frequency?: PeblScheduleFrequency;
  public startDate?: Date;
  public endCondition?: 'occurrences' | 'endDate';
  public occurrences?: number;
  public endDate?: Date;
}

export class PEBLPaymentRequest {
  public name: string = '';
  public monekId: string = '';
  public buid: string = '';
  public amount: number = 0; // major
  public paymentDetail: string = '';
  public currencyCode: string = '826';
  public countryCode: string = '826';
  public settle: string = '';
  public cof: boolean = false;
  public method: PaymentMethod | null = null;
  public OperatorId: string;
  public OperatorName: string;
  public OperatorEmail: string;
  public merchantName: string;
  public SendAt?: string;
  public Schedule?: PaymentScheduleRequest;

  public build(data: PEBLFormData): PEBLPaymentRequest {
    this.name = data.name;
    this.monekId = data.monekId;
    this.buid = data.buid;
    this.amount = Math.round(data.amount * 100); // need to send in minor currency
    this.paymentDetail = data.paymentDetail;
    this.currencyCode = data.currencyCode;
    this.countryCode = data.countryCode;
    this.settle = data.settle;
    this.cof = data.cof;
    this.OperatorId = data.OperatorId;
    this.OperatorName = data.OperatorName;
    this.OperatorEmail = data.OperatorEmail;
    this.merchantName = data.merchantName;
    this.method = new PaymentMethod(
      Number.parseInt(data.preparedPaymentMinutes),
      data.method,
      data.phoneNumber,
      data.emailAddress,
    );

    if (data.scheduleEnabled) {
      if (data.scheduleMode === 'once' && data.sendAt) {
        this.SendAt = toNaiveLocalIso(data.sendAt);
      } else if (
        data.scheduleMode === 'recurring' &&
        data.startDate &&
        data.frequency
      ) {
        this.Schedule = {
          Frequency: data.frequency,
          StartDate: toNaiveLocalIso(data.startDate),
          ...(data.endCondition === 'occurrences'
            ? { Occurrences: data.occurrences }
            : data.endDate
              ? { EndDate: toNaiveLocalIso(endOfDay(data.endDate)) }
              : {}),
        };
      }
    }

    return this;
  }

  public copy(existingLink: PEBLPaymentLink): PEBLPaymentRequest {
    const diff = Math.abs(
      new Date(existingLink.linkCreated).getTime() -
        new Date(existingLink.linkExpiration).getTime(),
    );
    const minutes = Math.floor(diff / 60000);

    this.name = existingLink.name;
    this.amount = existingLink.amount;
    this.paymentDetail = existingLink.paymentDetail;
    this.currencyCode = existingLink.currencyCode;
    this.countryCode = existingLink.countryCode;
    this.settle = existingLink.settle;
    this.OperatorId = existingLink.operatorId;
    this.OperatorName = existingLink.operatorName;
    this.OperatorEmail = existingLink.operatorEmail;
    this.merchantName = existingLink.merchantName;
    this.method = new PaymentMethod(
      minutes,
      existingLink.delivery,
      existingLink.phoneNumber,
      existingLink.emailAddress,
    );

    return this;
  }
}

export class PaymentMethod {
  constructor(
    public preparedPaymentMinutes: number,
    public delivery: string = '',
    public phoneNumber: string = '',
    public emailAddress: string = '',
    public status: string = '',
    public type: string = '',
    public paidDate?: Date,
  ) {}
}

export interface PaymentAuditEvent {
  crossReference?: string;
  status?: string;
  responseCode?: string;
  message?: string;
  createdDate?: Date;
}

export class PEBLPaymentLink {
  public link: string = '';
  public amount: number = 0;
  public linkCreated: Date = new Date();
  public linkExpiration: Date = new Date();
  public currencyCode: string = '';
  public currencyCodeIso: string = '';
  public countryCode: string = '';
  public settle: string = '';
  public status: 'Sent' | 'Cancelled' | 'Expired' | 'Authorised';
  public delivery: 'email' | 'sms' | 'manual';
  public name: string = '';
  public phoneNumber: string = '';
  public emailAddress: string = '';
  public paymentReference: string = '';
  public paymentDetail: string = '';
  public storeCredentials: boolean = false;
  public debtRepayment: boolean = false;
  public paymentChannel: string = '';
  public cancellationReason: string;
  // camelCase to match the Service.Payments response contract
  public operatorId: string;
  public operatorName: string;
  public operatorEmail: string;
  public merchantName: string;
  public auditHistory: PaymentAuditEvent[] = [];
}

export class PaymentLinkList {
  public lastEvaluatedKey: string = '';
  public paymentRequests: PEBLPaymentLink[] = [];
}

export type PeblScheduleFrequency = 'daily' | 'weekly' | 'monthly';
export type PeblScheduleStatus =
  | 'Active'
  | 'Sending'
  | 'Completed'
  | 'Cancelled'
  | 'Failed';
export type PeblScheduleType = 'OneOff' | 'Recurring';

export const PEBL_SCHEDULE_STATUSES: PeblScheduleStatus[] = [
  'Active',
  'Sending',
  'Completed',
  'Cancelled',
  'Failed',
];

// PascalCase to match the Service.Payments request contract
export interface PaymentScheduleRequest {
  Frequency: PeblScheduleFrequency;
  StartDate: string; // naive local wall-clock, interpreted as Europe/London by the API
  Occurrences?: number; // 1-52, mutually exclusive with EndDate
  EndDate?: string;
}

// 202 body returned by the create endpoint when SendAt/Schedule is present
export interface ScheduleResponse {
  scheduleReference: string;
  status: PeblScheduleStatus;
  scheduleType: PeblScheduleType;
  nextSendAt: string;
  frequency?: PeblScheduleFrequency;
  occurrencesTotal?: number;
}

export function isScheduleResponse(
  resp: PEBLPaymentResponse | ScheduleResponse,
): resp is ScheduleResponse {
  return (resp as ScheduleResponse).scheduleReference !== undefined;
}

export interface ScheduleItem {
  buid: string;
  scheduleReference: string;
  status: PeblScheduleStatus;
  type: PeblScheduleType;
  nextSendAt: string;
  frequency?: PeblScheduleFrequency;
  startDate: string;
  endDate?: string;
  totalOccurrences?: number;
  occurrencesSent: number;
  attempts: number;
  lastSentAt?: string;
  lastPaymentReference?: string;
  lastError?: string;
  createdAt: string;
  cancellationReason?: string;
  name: string;
  amount: number; // minor units
  paymentDetail: string;
  settle: string;
  currencyCode: string; // numeric, e.g. '826'
  delivery: 'email' | 'sms';
  phoneNumber?: string;
  emailAddress?: string;
  preparedPaymentMinutes: number;
  operatorId?: string;
  operatorName?: string;
  operatorEmail?: string;
  merchantName?: string;
}

export interface ScheduleListResponse {
  schedules: ScheduleItem[];
  lastEvaluatedKey: string;
}

// One dispatched send of a schedule; audit events are fetched per-occurrence
export interface ScheduleOccurrence {
  occurrenceNumber: number;
  paymentReference: string;
  sentAt: string;
  status?: string;
}

export interface ScheduleOccurrenceListResponse {
  occurrences: ScheduleOccurrence[];
  lastEvaluatedKey: string;
}

// The API treats offset-less datetimes as Europe/London wall-clock, which keeps
// recurring sends at the same local time across DST - so never use toISOString here.
export function toNaiveLocalIso(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(
    d.getDate(),
  )}T${p(d.getHours())}:${p(d.getMinutes())}:00`;
}

export function endOfDay(d: Date): Date {
  const end = new Date(d);
  end.setHours(23, 59, 0, 0);
  return end;
}
